/**
 * Zustand store that tracks the status of long-running agent operations
 * (backtest, grid_search, walk_forward) so the AppHeader can show a live indicator.
 */

import { create } from 'zustand';

export type AgentOperationKind = 'backtest' | 'grid_search' | 'walk_forward';

interface AgentStatusState {
  status: 'idle' | AgentOperationKind;
  label: string;
  current: number;
  total: number;
  percent: number;
  runId?: string;

  // Actions
  start: (kind: AgentOperationKind, label: string, total: number, runId?: string) => void;
  updateProgress: (current: number, percent: number) => void;
  complete: () => void;
  fail: (errMsg: string) => void;
  /**
   * Reset to idle. Semantically identical to `complete()`, but named for the
   * logout/login reset path (SHA-71) so a previous account's in-flight
   * runId/label doesn't linger in the AppHeader indicator.
   */
  reset: () => void;
}

const IDLE_STATE = {
  status: 'idle' as const,
  label: '',
  current: 0,
  total: 0,
  percent: 0,
  runId: undefined,
};

export const useAgentStatusStore = create<AgentStatusState>((set, get) => ({
  ...IDLE_STATE,

  start(kind, label, total, runId) {
    const current = get();
    if (current.status !== 'idle') {
      console.warn(
        `[agentStatusStore] start("${kind}") called while "${current.status}" is still active — overwriting.`,
      );
    }
    set({ status: kind, label, current: 0, total, percent: 0, runId });
  },

  updateProgress(current, percent) {
    set({ current, percent });
  },

  complete() {
    set(IDLE_STATE);
  },

  fail(_errMsg) {
    set(IDLE_STATE);
  },

  reset() {
    set(IDLE_STATE);
  },
}));
